/* ==========================================================================
   PLP shared — the consolidation layer every PLP prototype loads.

     import { initPlpShared } from '/src/plp-shared/plp-shared.js';
     initPlpShared();                     // 03, 11, 12, 13
     initPlpShared({ setQuickBuy: true }); // 14 · Scrub Sets

   Runs after the page's own classic scripts (it's imported from the module
   script that mounts the nav), so the product grid, picks carousel and Quick
   Buy already exist. Everything here layers onto the artifact's own class
   names instead of editing artifact-owned code, so a 3-way artifact merge
   (docs/plp-merge-bases/) never has to carry it. The NEW badge is CSS only
   (plp-shared.css).

   What's being decided: one Quick Buy language across PLP and PDP — the
   PDP's badge and labels — and whether a set can be sized, top and bottom,
   without leaving the grid. Both Quick Buys put real lines in the lab's
   shared bag (src/figs-nav/figs-cart.js): the button confirms, the modal
   leaves, and the bag opens on what was added.
   ========================================================================== */

import './plp-shared.css';
import { addToFigsBag, openFigsBag, figsSavedSize } from '/src/figs-nav/figs-nav.js';
import { SET_COMPONENTS as COMPONENTS, kitFor } from '/src/figs-nav/figs-sets.js';

const SIZE_NAMES = {
  XXS: 'XX-Small', XS: 'X-Small', S: 'Small', M: 'Medium', L: 'Large', XL: 'X-Large',
  '2XL': '2X-Large', '3XL': '3X-Large', '4XL': '4X-Large', '5XL': '5X-Large', '6XL': '6X-Large',
};
const PDP = '/experiments/10-pdp-prototype/';
const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------------------------------------------------------------------------
   4. PLP → PDP prototype links. A wearfigs.com product the lab has a PDP
   prototype for opens that prototype instead, in the card's colour (the PDP
   reads ?color=). Matches the exact product handle only — the mid-rise ruched
   Isabel and the Catarina Maternity top are different products.
   ------------------------------------------------------------------------ */
const PDP_ROUTES = [
  { handle: /\/products\/womens-catarina-scrub-top$/, page: PDP },
  { handle: /\/products\/womens-isabel-scrub-pants$/, page: `${PDP}pants.html` },
];

export function pdpFor(url) {
  if (!url) return null;
  let parsed;
  try { parsed = new URL(url, location.href); } catch { return null; }
  if (!/wearfigs\.com$/.test(parsed.hostname)) return null;
  const route = PDP_ROUTES.find((r) => r.handle.test(parsed.pathname));
  if (!route) return null;
  const color = slugify(parsed.searchParams.get('color'));
  return color ? `${route.page}?color=${color}` : route.page;
}

function linkToPdps(root = document) {
  root.querySelectorAll('[data-product-url], a[href*="wearfigs.com"]').forEach((el) => {
    const to = pdpFor(el.getAttribute('data-product-url') || el.getAttribute('href'));
    if (!to) return;
    if (el.hasAttribute('data-product-url')) el.setAttribute('data-product-url', to);
    if (el.tagName === 'A') {
      el.setAttribute('href', to);
      el.removeAttribute('target');
    }
  });
}

/* ---------------------------------------------------------------------------
   2. Variant labels, PDP style. The copy changes here; the type lives in
   plp-shared.css.
   ------------------------------------------------------------------------ */
const PANT = /\b(pants?|joggers?|leggings?|shorts?|scrubleggings)\b/i;
const typeHeading = (productName) => (PANT.test(productName) ? 'Length' : 'Size Type');

function onOpen(modal, fn) {
  let open = modal.classList.contains('is-open');
  new MutationObserver(() => {
    const now = modal.classList.contains('is-open');
    if (now && !open) fn();
    open = now;
  }).observe(modal, { attributes: true, attributeFilter: ['class'] });
}

function patchLabels(modal) {
  const colorName = modal.querySelector('[data-quickbuy-color-name]');
  const colorSuffix = colorName?.parentElement.querySelector('.quickbuy-modal__label-suffix');
  if (colorSuffix) {
    colorSuffix.textContent = 'Color';
    colorSuffix.classList.add('plp-qb-sup');
  }

  const typeName = modal.querySelector('[data-quickbuy-type-name]');
  const typeRow = typeName?.parentElement;
  let heading = null;
  if (typeRow) {
    typeRow.querySelector('.quickbuy-modal__label-suffix')?.classList.add('plp-qb-hidden');
    heading = document.createElement('span');
    heading.className = 'plp-qb-type-heading';
    heading.textContent = 'Size Type: ';
    typeRow.insertBefore(heading, typeName);
  }

  const sizeLabel = modal.querySelector('.quickbuy-modal__select-label');
  const sizeGrid = modal.querySelector('[data-quickbuy-sizes]');
  sizeGrid?.addEventListener('click', () => {
    // runs after the page's own handler (bubbling), so the class is current
    const picked = sizeGrid.querySelector('.quickbuy-modal__size-option--selected');
    if (picked && sizeLabel) {
      const s = picked.textContent.trim();
      sizeLabel.textContent = `Size: ${SIZE_NAMES[s] || s}`;
    }
  });

  onOpen(modal, () => {
    if (sizeLabel) sizeLabel.textContent = 'Select Size';
    const name = modal.querySelector('[data-quickbuy-name]')?.textContent || '';
    if (heading) heading.textContent = `${typeHeading(name)}: `;
  });
}

/* ---------------------------------------------------------------------------
   3. Sets Quick Buy — size both products of a set without leaving the grid.
   Same garments, sizes and second variants as the Sets PDP's kit drawer
   (10-pdp-prototype/data.js SET_COMPONENTS / KITS); a card is matched to its
   kit by name. VIEW DETAILS opens each garment's own PDP (the lab's prototype
   where there is one).
   ------------------------------------------------------------------------ */
/* the garments, their live prices and photos, and which card is which set,
   live in src/figs-nav/figs-sets.js — shared with the bag, which splits any
   set that still arrives as one line. The Rafaela Set is the Rafaela top
   with the Kade straight leg. */
const MISSING = {
  size: () => 'Please select a size to add this to your bag.',
  type: (c) => `Please select a ${c.typeLabel.toLowerCase()} to add this to your bag.`,
};
const priceOf = (text) => Number((String(text).match(/\d+(?:\.\d+)?/) || [0])[0]);
/* the bag opens as the modal finishes leaving (its 0.55s slide) */
const BAG_AFTER_CLOSE_MS = 450;
const tm = (s) => s.replace(/™/g, '<span style="font-family:\'Roboto Mono\',monospace;font-weight:700">™</span>');

function itemHTML(c) {
  return `
  <section class="plp-set-qb__item" data-set-item="${c.id}" aria-labelledby="plp-set-${c.id}-name">
    <div class="plp-set-qb__card">
      <span class="plp-set-qb__media"><img class="plp-set-qb__ghost" alt="" decoding="async" data-set-ghost></span>
      <div class="plp-set-qb__content">
        <span class="plp-set-qb__role">The ${c.role}</span>
        <h3 class="plp-set-qb__name" id="plp-set-${c.id}-name">${tm(c.name)}</h3>
        <a class="plp-set-qb__details" data-set-details>View Details</a>
      </div>
    </div>
    <div class="plp-set-qb__group" data-set-group="size">
      <p class="plp-set-qb__label" id="plp-set-${c.id}-size" data-set-label="size">Select Size</p>
      <div class="quickbuy-modal__size-grid plp-set-qb__grid" role="radiogroup" aria-labelledby="plp-set-${c.id}-name plp-set-${c.id}-size" data-set-grid="size">
        ${c.sizes.map((s) => `<button type="button" class="quickbuy-modal__size-option" role="radio" aria-checked="false" data-value="${s}"><span>${s}</span></button>`).join('')}
      </div>
      <p class="plp-set-qb__error" data-set-error="size" hidden>${MISSING.size(c)}</p>
    </div>
    <div class="plp-set-qb__group" data-set-group="type"${c.types.length < 2 ? ' hidden' : ''}>
      <p class="plp-set-qb__label" id="plp-set-${c.id}-type" data-set-label="type">Select ${c.typeLabel}</p>
      <div class="quickbuy-modal__type-grid plp-set-qb__grid" role="radiogroup" aria-labelledby="plp-set-${c.id}-name plp-set-${c.id}-type" data-set-grid="type">
        ${c.types.map((t) => `<button type="button" class="quickbuy-modal__type-option" role="radio" aria-checked="false" data-value="${t}"><span>${t}</span></button>`).join('')}
      </div>
      <p class="plp-set-qb__error" data-set-error="type" hidden>${MISSING.type(c)}</p>
    </div>
  </section>`;
}

function initSetQuickBuy(modal) {
  const form = modal.querySelector('.quickbuy-modal__form');
  const sizeGrid = modal.querySelector('[data-quickbuy-sizes]');
  const typeGrid = modal.querySelector('[data-quickbuy-types]');
  const defaults = [sizeGrid, typeGrid].map((g) => g?.closest('.quickbuy-modal__section')).filter(Boolean);
  const colorName = modal.querySelector('[data-quickbuy-color-name]');
  const addBtn = modal.querySelector('.quickbuy-modal__add-btn');
  const closeBtn = modal.querySelector('[data-quickbuy-close]');
  if (!form || !addBtn) return;

  const root = document.createElement('div');
  root.className = 'plp-set-qb';
  root.hidden = true;
  root.setAttribute('data-plp-set-qb', '');
  defaults[0] ? defaults[0].before(root) : form.append(root);

  let lastCard = null;
  let parts = [];
  let cardSlug = 'navy';
  let busy = false;

  /* which card opened Quick Buy — capture phase, ahead of the page's own
     handler, which stops propagation */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.product-card__quickbuy, .product-card__quick-buy');
    if (trigger) lastCard = trigger.closest('.product-card');
  }, true);

  const label = (p, key, v) => (key === 'size'
    ? (v ? `Size: ${SIZE_NAMES[v] || v}` : 'Select Size')
    : (v ? `${p.c.typeLabel}: ${v}` : `Select ${p.c.typeLabel}`));

  function syncGhosts() {
    const slug = slugify(colorName?.textContent) || cardSlug;
    parts.forEach((p) => {
      p.ghost.onerror = () => { p.ghost.onerror = null; p.ghost.src = p.c.ghost(cardSlug); };
      const src = p.c.ghost(slug);
      if (p.ghost.getAttribute('src') !== src) p.ghost.src = src;
      p.details.href = p.c.page(slug);
      if (/^https?:/.test(p.details.href) && !p.details.href.startsWith(location.origin)) {
        p.details.target = '_blank';
        p.details.rel = 'noopener';
      } else {
        p.details.removeAttribute('target');
      }
    });
  }

  function clearError(p, key) {
    p.error[key].hidden = true;
    p.grid[key].removeAttribute('data-invalid');
  }

  function render(kit) {
    parts = kit.components.map((id) => ({ c: COMPONENTS[id], sel: { size: null, type: null } }));
    root.innerHTML = parts.map((p) => itemHTML(p.c)).join('');
    parts.forEach((p) => {
      const el = root.querySelector(`[data-set-item="${p.c.id}"]`);
      Object.assign(p, {
        el,
        ghost: el.querySelector('[data-set-ghost]'),
        details: el.querySelector('[data-set-details]'),
        grid: { size: el.querySelector('[data-set-grid="size"]'), type: el.querySelector('[data-set-grid="type"]') },
        label: { size: el.querySelector('[data-set-label="size"]'), type: el.querySelector('[data-set-label="type"]') },
        error: { size: el.querySelector('[data-set-error="size"]'), type: el.querySelector('[data-set-error="type"]') },
      });
      if (p.c.types.length < 2) p.sel.type = p.c.types[0];
      ['size', 'type'].forEach((key) => {
        const selected = key === 'size' ? 'quickbuy-modal__size-option--selected' : 'quickbuy-modal__type-option--selected';
        p.grid[key].addEventListener('click', (e) => {
          const tile = e.target.closest('[data-value]');
          if (!tile) return;
          p.sel[key] = tile.dataset.value;
          p.grid[key].querySelectorAll('[data-value]').forEach((t) => {
            const on = t === tile;
            t.classList.toggle(selected, on);
            t.setAttribute('aria-checked', String(on));
          });
          p.label[key].textContent = label(p, key, p.sel[key]);
          clearError(p, key);
        });
      });
    });
    syncGhosts();
  }

  /* swatch changes repaint every garment's ghost — after the page's own
     handler has updated the colour name */
  modal.addEventListener('click', (e) => {
    if (!root.hidden && e.target.closest('.quickbuy-modal__swatch')) syncGhosts();
  });

  onOpen(modal, () => {
    busy = false;
    const name = lastCard?.querySelector('.product-card__name')?.textContent.trim() || '';
    const kit = kitFor(name);
    root.hidden = !kit;
    defaults.forEach((s) => s.classList.toggle('plp-qb-hidden', Boolean(kit)));
    if (!kit) { parts = []; return; }
    cardSlug = slugify(name.split('—').pop()) || 'navy';
    render(kit);
  });

  /* ADD TO BAG: every product needs a size and its second variant. Missing
     picks don't fail silently — the first one scrolls into view, shakes and
     says what it needs; complete, the button confirms, both garments go in
     the bag as their own lines (each at its live price, with its on-model
     photo in the chosen colour — never the ghost), and the bag opens once
     the modal has gone. Capture phase
     so a set never falls through to the default. */
  addBtn.addEventListener('click', (e) => {
    if (root.hidden || !parts.length) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (busy) return;
    const missing = parts.flatMap((p) => ['size', 'type'].filter((k) => !p.sel[k]).map((k) => [p, k]));
    if (missing.length) {
      missing.forEach(([p, k]) => {
        p.error[k].hidden = false;
        p.grid[k].setAttribute('data-invalid', '');
      });
      const [p, k] = missing[0];
      p.grid[k].closest('.plp-set-qb__group').scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      p.grid[k].classList.remove('is-shaking');
      void p.grid[k].offsetWidth;
      p.grid[k].classList.add('is-shaking');
      p.grid[k].querySelector('[data-value]')?.focus({ preventScroll: true });
      return;
    }
    busy = true;
    const color = colorName?.textContent.trim() || '';
    const slug = slugify(color) || cardSlug;
    addToFigsBag(parts.map((p) => ({
      id: p.c.id, name: p.c.name, color, size: p.sel.size, type: p.sel.type, price: p.c.price,
      image: p.c.model(slug), fallback: p.c.model(cardSlug),
      url: /^https?:/.test(p.details.getAttribute('href') || '') ? '' : p.details.href,
    })), { open: false });
    const label = addBtn.innerHTML;
    addBtn.innerHTML = '<span>Added to Bag</span>';
    setTimeout(() => {
      closeBtn?.click();
      setTimeout(openFigsBag, BAG_AFTER_CLOSE_MS);
      setTimeout(() => { addBtn.innerHTML = label; busy = false; }, 400);
    }, reducedMotion() ? 600 : 1100);
  }, true);

  root.addEventListener('animationend', (e) => e.target.classList?.remove('is-shaking'));
}

/* ---------------------------------------------------------------------------
   5. Quick Buy → bag. The artifact's ADD TO BAG had no handler. It now needs
   a size, like the PDP's Quick Buy: without one the size grid shakes and says
   so. With one, the product goes in the shared bag in the modal's colour,
   size and type, the button confirms, the modal leaves and the bag opens.
   The photo is the card's, which is the colour the card showed.
   ------------------------------------------------------------------------ */
function initQuickBuyBag(modal) {
  const addBtn = modal.querySelector('.quickbuy-modal__add-btn');
  const sizeGrid = modal.querySelector('[data-quickbuy-sizes]');
  const typeGrid = modal.querySelector('[data-quickbuy-types]');
  const closeBtn = modal.querySelector('[data-quickbuy-close]');
  if (!addBtn || !sizeGrid) return;
  const text = (sel) => modal.querySelector(sel)?.textContent.trim() || '';

  const error = document.createElement('p');
  error.className = 'plp-set-qb__error plp-qb-error';
  error.hidden = true;
  error.textContent = MISSING.size();
  sizeGrid.after(error);
  const clearError = () => {
    error.hidden = true;
    sizeGrid.removeAttribute('data-invalid');
  };
  sizeGrid.addEventListener('click', () => {
    if (sizeGrid.querySelector('.quickbuy-modal__size-option--selected')) clearError();
  });
  sizeGrid.addEventListener('animationend', () => sizeGrid.classList.remove('is-shaking'));
  onOpen(modal, clearError);

  let busy = false;
  addBtn.addEventListener('click', (e) => {
    if (modal.querySelector('[data-plp-set-qb]:not([hidden])')) return; // a set: its own add (above)
    e.preventDefault();
    if (busy) return;
    const size = sizeGrid.querySelector('.quickbuy-modal__size-option--selected')?.textContent.trim();
    if (!size) {
      error.hidden = false;
      sizeGrid.setAttribute('data-invalid', '');
      sizeGrid.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      sizeGrid.classList.remove('is-shaking');
      void sizeGrid.offsetWidth;
      sizeGrid.classList.add('is-shaking');
      return;
    }
    busy = true;
    const image = modal.querySelector('[data-quickbuy-image="0"]');
    const view = modal.querySelector('[data-quickbuy-view-link]');
    addToFigsBag({
      name: text('[data-quickbuy-name]'),
      color: text('[data-quickbuy-color-name]'),
      size,
      type: typeGrid?.querySelector('.quickbuy-modal__type-option--selected')?.textContent.trim() || '',
      price: priceOf(text('[data-quickbuy-price]')),
      image: image?.currentSrc || image?.src || '',
      url: view?.href?.startsWith(location.origin) ? view.href : '',
    }, { open: false });
    const label = addBtn.innerHTML;
    addBtn.innerHTML = '<span>Added to Bag</span>';
    setTimeout(() => {
      closeBtn?.click();
      setTimeout(openFigsBag, BAG_AFTER_CLOSE_MS);
      setTimeout(() => { addBtn.innerHTML = label; busy = false; }, 400);
    }, reducedMotion() ? 600 : 1100);
  });
}

/* ---------------------------------------------------------------------------
   6. Saved sizes — a signed-in shopper (the nav's log-in flow) finds the
   sizes saved in My Account → Preferences already chosen when Quick Buy
   opens: the product's size and size type / length, or each garment's in a
   set. They're picked the way a tap would (so every label follows), after
   the page has reset the modal — this observer is registered last. A guest
   opens it empty, as before.
   ------------------------------------------------------------------------ */
function preselectSavedSizes(modal) {
  const options = (grid) => [...(grid?.children || [])].map((b) => [b.textContent.trim(), b]);
  onOpen(modal, () => {
    const set = modal.querySelector('[data-plp-set-qb]:not([hidden])');
    if (set) {
      set.querySelectorAll('[data-set-item]').forEach((item) => {
        const c = COMPONENTS[item.dataset.setItem];
        const s = c && figsSavedSize({ role: c.role, name: c.name, sizes: c.sizes, types: c.types });
        if (!s) return;
        item.querySelector(`[data-set-grid="size"] [data-value="${s.size}"]`)?.click();
        if (s.type) item.querySelector(`[data-set-grid="type"] [data-value="${s.type}"]`)?.click();
      });
      return;
    }
    const sizes = options(modal.querySelector('[data-quickbuy-sizes]'));
    const types = options(modal.querySelector('[data-quickbuy-types]'));
    const s = figsSavedSize({
      name: modal.querySelector('[data-quickbuy-name]')?.textContent.trim(),
      url: modal.querySelector('[data-quickbuy-view-link]')?.href,
      sizes: sizes.map(([v]) => v),
      types: types.map(([v]) => v),
    });
    if (!s) return;
    sizes.find(([v]) => v === s.size)?.[1].click();
    if (s.type) types.find(([v]) => v === s.type)?.[1].click();
  });
}

/* ---------------------------------------------------------------------------
   7. Lab cross-links (treed, 2026-10-18) — the artifact's link-shaped
   things without a destination get the lab page that shows them: the
   breadcrumb root and Shop Men / Shop Women, the Scrub Sets PLP's set tiles
   (each set's PDP), the Scrub Pants PLP's Isabel banner (the Isabel PDP).
   Quick Buy's View Product, when it's still wearfigs.com, opens in a new tab
   so the lab stays where it was.
   ------------------------------------------------------------------------ */
const LAB = {
  scrubPants: '/experiments/03-plp-prototype/',
  menCore: '/experiments/12-plp-core/',
  setPdp: `${PDP}set.html`,
  pantPdp: `${PDP}pants.html`,
};
const CRUMB_HREFS = { 'Scrub Pants': LAB.scrubPants, Men: LAB.menCore };
const CROSS_HREFS = { 'Shop Men': LAB.menCore, 'Shop Women': LAB.scrubPants };
const CATEGORY_HREFS = {
  'Jogger Set': `${LAB.setPdp}?set=jogger`,
  'Wide-Leg Set': `${LAB.setPdp}?set=wide-leg`,
  'Straight-Leg Set': `${LAB.setPdp}?set=straight-leg`,
  'Limited Edition Set': `${LAB.setPdp}?color=bonsai`, // the set its card shows
};
function crossLinks(modal) {
  const set = (sel, hrefs) => document.querySelectorAll(sel).forEach((a) => {
    const href = hrefs[a.textContent.trim()];
    if (href && href !== location.pathname) a.href = href;
  });
  set('.crumb-link:not([href])', CRUMB_HREFS);
  set('.shop-men-link a', CROSS_HREFS);
  set('.category-card:not([href])', CATEGORY_HREFS);
  document.querySelectorAll('.split-banner-card:not([href])').forEach((a) => {
    if (/isabel/i.test(a.textContent)) a.href = LAB.pantPdp;
  });
  const view = modal?.querySelector('[data-quickbuy-view-link]');
  if (view) {
    onOpen(modal, () => {
      const external = /^https?:/.test(view.href) && !view.href.startsWith(location.origin);
      if (external) Object.assign(view, { target: '_blank', rel: 'noopener' });
      else { view.removeAttribute('target'); view.removeAttribute('rel'); }
    });
  }
}

/* ------------------------------------------------------------------------ */
export function initPlpShared({ setQuickBuy = false } = {}) {
  linkToPdps();
  const modal = document.querySelector('.quickbuy-modal');
  crossLinks(modal);
  if (modal) {
    patchLabels(modal);
    if (setQuickBuy) initSetQuickBuy(modal);
    initQuickBuyBag(modal);
    preselectSavedSizes(modal);
  }
}
